import { ProcessingStatus } from './ProcessingStatus';
import { useLocation } from 'react-router-dom';
import { ChromeIcon } from '../ChromeIcon';
import { resolvePageShortTitle, resolvePageTitle } from '@/lib/pageTitles';
import { PageHeaderIcon } from './PageHeaderIcon';
import { usePageHeaderSlots } from './PageHeaderSlots';
import styles from './PageHeaderBar.module.css';
import { HeaderSearch } from './HeaderSearch';
import { useIsMobile } from '@/hooks/useMediaQuery';

interface PageHeaderBarProps {
  onMenuClick?: () => void;
  onHelpClick?: () => void;
  /** True while the desktop rail is collapsed — grows the expand button in. */
  sidebarCollapsed?: boolean;
  onExpandSidebar?: () => void;
}

/**
 * The authenticated shell's page header bar — the strip above every contractor
 * page carrying the route's clay-relief mark + title on the left and Settings + Help on the right,
 * closed by the hairline that continues the sidebar header's own bottom border
 * (one grid line across the window).
 *
 * It is the single source of the visible page title: pages do NOT render their
 * own <h1>. A page may replace the crumb or add trailing actions via
 * PageHeaderSlots (the projected crumb must then carry the h1 — the Aria chat
 * does this). Its height is --page-header-h (globals.css), which pages doing
 * 100dvh math subtract. At <=768px it also hosts the drawer hamburger, which is
 * why it replaced the old mobile-only Header component.
 *
 * It also owns the only way back from a collapsed sidebar: the expand button
 * stays mounted at zero width and grows in when the rail is away, so revealing
 * it doesn't shove the title sideways.
 */
export function PageHeaderBar({
  onMenuClick,
  onHelpClick,
  sidebarCollapsed = false,
  onExpandSidebar,
}: PageHeaderBarProps) {
  const { pathname } = useLocation();
  const slots = usePageHeaderSlots();
  const title = resolvePageTitle(pathname);
  const isMobile = useIsMobile();
  // Phones show the short name ("Attention"); the h1 still reads the full one.
  const shortTitle = isMobile ? resolvePageShortTitle(pathname) : undefined;

  return (
    <header className={styles.bar}>
      {/* data-drawer-trigger: where focus returns when the phone drawer closes. */}
      <button
        type="button"
        className={styles.menuBtn}
        aria-label="Open menu"
        onClick={onMenuClick}
        data-drawer-trigger=""
      >
        <ChromeIcon name="menu" size={22} />
      </button>

      {/* data-sidebar-expand: the Sidebar hands focus here when the rail
          collapses under it, and takes it back from here on expand. */}
      {onExpandSidebar && (
        <button
          type="button"
          className={[styles.expandBtn, sidebarCollapsed ? styles.expandBtnIn : ''].join(' ')}
          aria-label="Open sidebar"
          onClick={onExpandSidebar}
          tabIndex={sidebarCollapsed ? 0 : -1}
          aria-hidden={!sidebarCollapsed}
          data-sidebar-expand=""
        >
          <ChromeIcon name="sidebarOpen" size={18} tone="muted" />
          <span className={styles.expandTip} role="tooltip">Open sidebar</span>
        </button>
      )}

      <div className={styles.identity}>
      {slots?.crumb ?? (
        <div className={styles.crumb}>
          <PageHeaderIcon pathname={pathname} className={styles.crumbIcon} />
          {title && (
            <h1 className={styles.crumbTitle}>
              {shortTitle ? (
                <>
                  <span aria-hidden="true">{shortTitle}</span>
                  <span className="sr-only">{title}</span>
                </>
              ) : (
                title
              )}
            </h1>
          )}
        </div>
      )}

      </div>
      {!isMobile && <HeaderSearch />}
      <div className={styles.trailing}>
        <ProcessingStatus />
        {slots?.actions}
        {isMobile && <HeaderSearch />}
        {/* data-help-trigger: where focus lands when Help closes and its
            opener has gone (the peek tab retires once its video is watched). */}
        <button
          type="button"
          className={styles.helpBtn}
          aria-label="Help"
          onClick={onHelpClick}
          data-help-trigger=""
        >
          <ChromeIcon name="help" size={15} />
          <span className={styles.helpLabel}>Help</span>
        </button>
      </div>
    </header>
  );
}
